/* ============================================================================
 * MARKS — the Supercompute icon set, as data
 * ----------------------------------------------------------------------------
 * Ported verbatim from the locked imaging style sheet §7 (`icons`), which is
 * the design source for the suite: 24 px box, 1.5 px stroke, round caps and
 * joins, no fills, one stroke weight, currentColor.
 *
 *   const FIRST  = 16 function marks — one per desk in `apps/registry.ts`
 *   BRAND_MARKS  =  3 variants of the dish, the brand mark itself
 *
 * This module is deliberately React-free and dependency-free: the conformance
 * script, the SVG exporter and the components all read the same table, so the
 * page and the build cannot drift apart.
 *
 * Colour is never baked in. Structurally the mark is one stroke path; the
 * palette decides what that stroke is:
 *
 *   rest    #6fa3e5  (--ui-info)    7.0:1 on navy
 *   active  #c9a33a  (--ui-accent)  7.7:1 on navy
 *   bright  #050b1c  (ink)         16.7:1 on cream
 *
 * Brass on cream is 2.0:1 — the mark never sits brass-on-light.
 * ========================================================================= */

/** One drawing primitive. Groups exist only for the badge's centring scale. */
export type MarkPrimitive =
  | { readonly kind: 'path'; readonly d: string; readonly opacity?: number }
  | { readonly kind: 'circle'; readonly cx: number; readonly cy: number; readonly r: number }
  | { readonly kind: 'rect'; readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  | { readonly kind: 'group'; readonly transform: string; readonly children: readonly MarkPrimitive[] };

export interface FunctionMarkDef {
  readonly id: string;
  /** The `apps/registry.ts` app this mark belongs to. */
  readonly appId: string;
  readonly label: string;
  /** The desk's job, as the style sheet words it. */
  readonly use: string;
  readonly viewBox: string;
  readonly strokeWidth: number;
  readonly body: readonly MarkPrimitive[];
}

export interface BrandMarkDef {
  readonly variant: BrandMarkVariant;
  readonly label: string;
  readonly aria: string;
  readonly viewBox: string;
  readonly strokeWidth: number;
  readonly body: readonly MarkPrimitive[];
}

export const FUNCTION_MARKS = [
  {
    id: 'embassy',
    appId: 'embassy',
    label: 'Embassy',
    use: 'Sovereign pass, credentials',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M12 3 19.5 5.6V12c0 4.5-3.3 7.4-7.5 8.7C7.8 19.4 4.5 16.5 4.5 12V5.6Z' },
      { kind: 'circle', cx: 12, cy: 11.4, r: 2.3 },
    ],
  },
  {
    id: 'news-desk',
    appId: 'newsdesk',
    label: 'News desk',
    use: 'Presence, dispatch, lookup',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M5.5 3.5h13v17h-13z' },
      { kind: 'path', d: 'M9 8.5h6M9 12h6M9 15.5h3.5' },
    ],
  },
  {
    id: 'trade-desk',
    appId: 'tradedesk',
    label: 'Trade desk',
    use: 'Treasury, splits',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M12 3.5v17' },
      { kind: 'rect', x: 8.5, y: 7.5, w: 7, h: 9 },
    ],
  },
  {
    id: 'school',
    appId: 'school',
    label: 'School',
    use: 'Cohorts, tracks, certification',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 6.6C6.4 5.1 9.4 5.1 12 6.6c2.6-1.5 5.6-1.5 8 0V18c-2.4-1.5-5.4-1.5-8 0-2.6-1.5-5.6-1.5-8 0Z' },
      { kind: 'path', d: 'M12 6.6V18' },
    ],
  },
  {
    id: 'channel',
    appId: 'channel',
    label: 'Channel',
    use: 'Live channels, syndication',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M7.4 9.6A6 6 0 0 1 16.6 9.6' },
      { kind: 'path', d: 'M4.6 6.4a10 10 0 0 1 14.8 0' },
      { kind: 'path', d: 'M9.6 17.4h4.8' },
    ],
  },
  {
    id: 'live-tv',
    appId: 'live-tv',
    label: 'Live TV',
    use: 'Broadcast player',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M3.5 8.5h17v12h-17z' },
      { kind: 'path', d: 'M8 8.5 10 4.5M16 8.5 14 4.5' },
      { kind: 'path', d: 'M10.5 12.5l4.5 2.5-4.5 2.5z' },
    ],
  },
  {
    id: 'storefront',
    appId: 'storefront',
    label: 'Storefront',
    use: 'Merch and digital goods',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 9.2 5.6 4.5h12.8L20 9.2' },
      { kind: 'path', d: 'M5 9.2V20h14V9.2' },
      { kind: 'path', d: 'M10 20v-5.5h4V20' },
    ],
  },
  {
    id: 'wallet',
    appId: 'wallet',
    label: 'Wallet',
    use: 'Assets, consent, waivers',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M3.5 7.5h17v12h-17z' },
      { kind: 'path', d: 'M3.5 11.5h17' },
      { kind: 'circle', cx: 16.6, cy: 15.5, r: 1.2 },
    ],
  },
  {
    id: 'home-office',
    appId: 'home-office',
    label: 'Home office',
    use: 'Runsheet, payables',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M8.5 4.5h7v6h-7z' },
      { kind: 'path', d: 'M4 13.5h16' },
      { kind: 'path', d: 'M6 13.5V20M18 13.5V20' },
    ],
  },
  {
    id: 'reserve',
    appId: 'reserve-capital',
    label: 'Reserve',
    use: 'Value, splits, reserve',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4.5 5h15v15h-15z' },
      { kind: 'circle', cx: 12, cy: 12.5, r: 3.5 },
      { kind: 'path', d: 'M12 9v-4M12 20v-4' },
    ],
  },
  {
    id: 'calendar',
    appId: 'calendar',
    label: 'Calendar',
    use: 'Events, guild, guestbook',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 6h16v14.5H4z' },
      { kind: 'path', d: 'M4 10.5h16' },
      { kind: 'path', d: 'M8.5 3.5v3M15.5 3.5v3' },
      { kind: 'circle', cx: 12, cy: 15.5, r: 1.2 },
    ],
  },
  {
    id: 'community',
    appId: 'community',
    label: 'Community',
    use: 'Members, tiers, invites',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'circle', cx: 12, cy: 6.8, r: 2.3 },
      { kind: 'circle', cx: 6.6, cy: 17, r: 2.3 },
      { kind: 'circle', cx: 17.4, cy: 17, r: 2.3 },
      { kind: 'path', d: 'M9.8 8.6 7.8 14.9M14.2 8.6l2 6.3M8.9 17h6.2' },
    ],
  },
  {
    id: 'vendor',
    appId: 'vendor',
    label: 'Vendor',
    use: 'Rate card, invoices',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4.5 8.5h15V20h-15z' },
      { kind: 'path', d: 'M4.5 12h15' },
      { kind: 'path', d: 'M9.5 12V20' },
    ],
  },
  {
    id: 'settings',
    appId: 'settings',
    label: 'Settings',
    use: 'Design language and skins',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 7.5h16M4 12h16M4 16.5h16' },
      { kind: 'circle', cx: 9, cy: 7.5, r: 1.8 },
      { kind: 'circle', cx: 15.5, cy: 12, r: 1.8 },
      { kind: 'circle', cx: 7.5, cy: 16.5, r: 1.8 },
    ],
  },
  {
    id: 'skin-builder',
    appId: 'skin-builder',
    label: 'Skin builder',
    use: 'Palette, safe switches',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 4h11v11H4z' },
      { kind: 'path', d: 'M9 9h11v11H9z' },
    ],
  },
  {
    id: 'immersion',
    appId: 'wizard',
    label: 'Immersion',
    use: 'Identity, container, roster',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M4 4h16v16H4z' },
      { kind: 'path', d: 'M12 8.5 15.5 12 12 15.5 8.5 12Z' },
    ],
  },
] as const satisfies readonly FunctionMarkDef[];

export type FunctionMarkId = (typeof FUNCTION_MARKS)[number]['id'];

export const BRAND_MARKS = [
  {
    variant: 'primary',
    label: 'Mark A · primary · dish + signal',
    aria: 'Supercompute mark — dish with feed arm and two signal arcs',
    viewBox: '0 0 32 32',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M8.6 14.2 Q10.2 25.4 21.4 23.8 Z' },
      { kind: 'path', d: 'M15 19 L19.2 13.4' },
      { kind: 'circle', cx: 19.6, cy: 13, r: 1.3 },
      { kind: 'path', d: 'M12.6 22.5 V26.5 M8.8 27.5 H18.4' },
      { kind: 'path', d: 'M24.5 12.1 A5 5 0 0 0 20.5 8.1' },
      { kind: 'path', d: 'M27.5 11.6 A8 8 0 0 0 21 5.1' },
    ],
  },
  {
    variant: 'badge',
    label: 'Mark B · badge · tiles ≥ 24 px',
    aria: 'Supercompute badge — the dish inside a ring, for app tiles',
    viewBox: '0 0 32 32',
    strokeWidth: 1.5,
    body: [
      { kind: 'circle', cx: 16, cy: 16, r: 14.5 },
      { kind: 'group', transform: 'translate(16 16) scale(0.84) translate(-16 -18)', children: [
          { kind: 'path', d: 'M8.6 14.2 Q10.2 25.4 21.4 23.8 Z' },
          { kind: 'path', d: 'M15 19 L19.2 13.4' },
          { kind: 'circle', cx: 19.6, cy: 13, r: 1.3 },
          { kind: 'path', d: 'M12.6 22.5 V26.5 M9.5 27.5 H17.5' },
        ] },
    ],
  },
  {
    variant: 'small',
    label: 'Reduction 16 px · arcs and base drop',
    aria: 'Supercompute mark, 16px reduction — signal arcs and base dropped',
    viewBox: '0 0 32 32',
    strokeWidth: 1.5,
    body: [
      { kind: 'path', d: 'M8.6 14.2 Q10.2 25.4 21.4 23.8 Z' },
      { kind: 'path', d: 'M15 19 L19.2 13.4' },
      { kind: 'circle', cx: 19.6, cy: 13, r: 1.3 },
      { kind: 'path', d: 'M12.6 22.5 V26.5' },
    ],
  },
] as const satisfies readonly BrandMarkDef[];

export type BrandMarkVariant = 'primary' | 'badge' | 'small';

export const FUNCTION_MARK_IDS = FUNCTION_MARKS.map((m) => m.id) as readonly FunctionMarkId[];

/** Canonical id, or the style sheet's own label. */
export function functionMark(id: string): FunctionMarkDef | undefined {
  const key = id.trim().toLowerCase();
  return (
    FUNCTION_MARKS.find((m) => m.id === key) ??
    FUNCTION_MARKS.find((m) => m.label.toLowerCase() === key)
  );
}

/**
 * A desk's own mark. Accepts the registry id (`newsdesk`, `reserve-capital`,
 * `wizard`), the mark id (`news-desk`, `reserve`, `immersion`) or the label.
 */
export function functionMarkForApp(appId: string): FunctionMarkDef | undefined {
  const key = appId.trim().toLowerCase();
  return (
    FUNCTION_MARKS.find((m) => m.appId === key) ??
    FUNCTION_MARKS.find((m) => m.id === key)
  );
}

export function brandMark(variant: BrandMarkVariant): BrandMarkDef {
  const found = BRAND_MARKS.find((m) => m.variant === variant);
  if (!found) throw new Error(`unknown brand mark variant: ${variant}`);
  return found;
}
